import React, { useEffect, useState } from "react";
import {
    Box,
    Divider,
    Grid,
    Link,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    Paper,
    Skeleton
} from '@mui/material';
import { observer } from "mobx-react-lite";
import { withRouter } from "react-router";
import {
    getSiteBillingPortal,
    getSitePaymentReport,
    getSiteSubscription
} from "../../../actions/internal/SiteActions";
import {
    getSiteBillingPortalById,
    getSiteById,
    getSiteInvoiceReportById,
    getSiteSubscriptionById
} from "../../../selectors/SiteSelectors";
import StyledSubHeader from "../../common/StyledSubHeader";
import StyledProgressCircle, { StyledLoadingSection } from "../../common/StyledProgressCircle";
import StyledChip from "../../common/StyledChip";
import StyledLineItem from "../../common/StyledLineItem";
import { ContainedButton, TooltipIconButton } from "../../common/StyledButtons";
import StyledPage from "../../common/StyledPage";
import StyledSection from "../../common/StyledSection";
import Colors from "../../../constants/Colors";
import { AssignmentOutlined, ReceiptOutlined } from "@mui/icons-material";
import { getUserId } from "../../../selectors/UserSelectors";
import { formatDateToFancyString } from "../../../utilities/DateUtilities";
import { SUMMARY_TITLE_FONT } from "../../../constants/Styles";

const ViewSiteBillingComponent = ({ match, history }) => {

    const [isLoading, setIsLoading] = useState(true);
    const [showAll, setShowAll] = useState(false);

    const siteId = match.params.site_id;

    // Raw selector values
    const rawInvoiceReport = getSiteInvoiceReportById(siteId);
    const rawSubscription = getSiteSubscriptionById(siteId);
    const rawBillingUrl = getSiteBillingPortalById(siteId);
    const rawSite = getSiteById(siteId);

    // Normalized safe objects
    const invoiceReport =
        rawInvoiceReport && rawInvoiceReport.report
            ? rawInvoiceReport
            : {
                  report: {
                      open_invoices: { invoices: [] },
                      paid_invoices: { invoices: [] }
                  }
              };

    const subscription =
        rawSubscription && rawSubscription.stripe_subscription
            ? rawSubscription
            : { stripe_subscription: null };

    const billingUrl =
        rawBillingUrl && rawBillingUrl.url
            ? rawBillingUrl
            : null;

    const site =
        rawSite && rawSite.site_service_modules
            ? rawSite
            : {
                  site_service_modules: {
                      consulting: {
                          general_consulting: {
                              pricing: { default: 0 }
                          }
                      }
                  }
              };

    useEffect(() => {
        loadData().then();
    }, []);

    const loadData = async () => {
        try {
            const userId = getUserId();
            await Promise.all([
                getSitePaymentReport(siteId),
                getSiteSubscription(userId, siteId),
                getSiteBillingPortal(userId, siteId)
            ]);
        } catch (ex) {
            console.error(ex);
        } finally {
            setIsLoading(false);
        }
    };

    const showLinkInNewTab = (url) => {
        if (!url) return;
        const win = window.open(url, '_blank');
        if (win) win.focus();
    };

    const humanizeStripeStatus = (status) => {
        if (!status || typeof status !== 'string') {
            return 'Unknown';
        }

        return status
            .replace(/_/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const renderLoadingContent = () => <StyledLoadingSection />;

    const renderPackageDetailsSection = () => {
        const stripeSub = subscription.stripe_subscription;

        const items =
            stripeSub &&
            stripeSub.items &&
            Array.isArray(stripeSub.items.data)
                ? stripeSub.items.data
                : [];

        let total = 0;
        if (stripeSub && stripeSub.status === 'active') {
            total = items.reduce((acc, val) => {
                const amount = val && val.price && val.price.unit_amount ? val.price.unit_amount : 0;
                const qty = val && val.quantity ? val.quantity : 0;
                return acc + amount * qty;
            }, 0);
        }

        const hourlyRate =
            site.site_service_modules &&
            site.site_service_modules.consulting &&
            site.site_service_modules.consulting.general_consulting &&
            site.site_service_modules.consulting.general_consulting.pricing &&
            site.site_service_modules.consulting.general_consulting.pricing.default
                ? site.site_service_modules.consulting.general_consulting.pricing.default
                : 0;

        return (
            <StyledSection
                title="Service Pricing"
                rightAdornment={
                    <StyledChip
                        text={
                            stripeSub &&
                            stripeSub.collection_method === "charge_automatically"
                                ? "Autopay On"
                                : "Autopay Off"
                        }
                        sx={{
                            backgroundColor:
                                stripeSub &&
                                stripeSub.collection_method === "charge_automatically"
                                    ? Colors.primaryColor
                                    : Colors.greyColor
                        }}
                    />
                }
            >
                {!stripeSub || stripeSub.status === 'canceled' ? (
                    <>
                        <h4 style={{ margin: 0, fontSize: '24px' }}>No Active Services...</h4>
                        <p>
                            It looks like you haven't choosen any services for your account yet.
                            Our services offer a valuable way to make your mark on the web.
                        </p>
                        <ContainedButton
                            onClick={() =>
                                showLinkInNewTab("https://consultwithkyle.com/appointments/quick-call")
                            }
                        >
                            Schedule A Call
                        </ContainedButton>
                    </>
                ) : (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', marginTop: '25px' }}>
                            <StyledLineItem
                                primary="Status"
                                secondary={humanizeStripeStatus(stripeSub.status)}
                                sx={{ flexDirection: 'column' }}
                                primarySx={{ fontSize: '20px' }}
                            />
                            <StyledLineItem
                                primary="Renewal Date"
                                secondary={formatDateToFancyString(stripeSub.current_period_end * 1000)}
                                sx={{ flexDirection: 'column' }}
                                primarySx={{ fontSize: '20px' }}
                            />
                            <StyledLineItem
                                primary="Hourly Rate"
                                secondary={`$${(hourlyRate / 100).toFixed(2)}/hour`}
                                sx={{ flexDirection: 'column' }}
                                primarySx={{ fontSize: '20px' }}
                            />
                            <div />
                        </Box>

                        <Box>
                            <div style={{ ...SUMMARY_TITLE_FONT, fontSize: '20px', marginTop: '20px' }}>
                                Item Breakdown
                            </div>
                            {items.map((item, index) => (
                                <div key={index}>
                                    {index !== 0 && <Divider />}
                                    <StyledLineItem
                                        sx={{ marginTop: '10px', marginBottom: '10px' }}
                                        primary={`${item.product.name} (x${item.quantity})`}
                                        secondary={`$${((item.price.unit_amount * item.quantity) / 100).toFixed(2)}`}
                                    />
                                </div>
                            ))}
                            <Divider sx={{ borderBottomWidth: 'medium' }} />
                            <StyledLineItem
                                sx={{ marginTop: '10px', marginBottom: '10px' }}
                                primary="Total"
                                secondary={`$${(total / 100).toFixed(2)}`}
                            />
                        </Box>
                    </>
                )}
            </StyledSection>
        );
    };

    const renderDueInvoicesSection = () => {
        const openInvoices = invoiceReport.report.open_invoices.invoices;

        return (
            <StyledSection title="Due Invoices">
                <Paper variant="outlined" sx={{ marginTop: '5px', paddingLeft: '10px', paddingRight: '10px' }}>
                    <List>
                        {openInvoices.map((invoice, index) => (
                            <ListItem
                                key={invoice.id}
                                dense
                                sx={{
                                    backgroundColor: '#fff',
                                    marginBottom: '5px',
                                    borderBottom:
                                        index !== openInvoices.length - 1
                                            ? '1px solid #e0e0e0'
                                            : 'none'
                                }}
                            >
                                <ListItemText
                                    primary={<span style={{ fontSize: 18 }}>{invoice.name}</span>}
                                    secondary={`$${(invoice.amount_due / 100).toFixed(2)}`}
                                />
                                <ListItemSecondaryAction>
                                    {invoice.project_id && (
                                        <TooltipIconButton
                                            text="View Project Details"
                                            icon={
                                                <AssignmentOutlined
                                                    sx={{ color: Colors.primaryColor }}
                                                    onClick={() =>
                                                        history.push(`/dashboard/site/${siteId}/projects/${invoice.project_id}`)
                                                    }
                                                />
                                            }
                                        />
                                    )}
                                    <TooltipIconButton
                                        text="View Invoice & Pay Now"
                                        icon={
                                            <ReceiptOutlined
                                                sx={{ color: Colors.primaryColor }}
                                                onClick={() => showLinkInNewTab(invoice.hosted_invoice_url)}
                                            />
                                        }
                                    />
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                        {openInvoices.length === 0 && (
                            <p>Woo-hoo! No invoices are due <span role="img" aria-label="party face">🥳</span></p>
                        )}
                    </List>
                </Paper>
            </StyledSection>
        );
    };

    const renderInvoiceHistorySection = () => {
        const invoices = invoiceReport.report.paid_invoices.invoices;
        const visibleInvoices = showAll ? invoices : invoices.slice(0, 5);

        return (
            <StyledSection title="Invoice History">
                <Paper variant="outlined" sx={{ marginTop: '5px', paddingLeft: '10px', paddingRight: '10px' }}>
                    <List>
                        {visibleInvoices.map((invoice, index) => (
                            <ListItem key={invoice.id} dense>
                                <ListItemText
                                    primary={invoice.name}
                                    secondary={`$${(invoice.amount_due / 100).toFixed(2)}`}
                                />
                                <ListItemSecondaryAction>
                                    <TooltipIconButton
                                        text="View Invoice"
                                        icon={
                                            <ReceiptOutlined
                                                sx={{ color: Colors.primaryColor }}
                                                onClick={() => showLinkInNewTab(invoice.hosted_invoice_url)}
                                            />
                                        }
                                    />
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                        {invoices.length === 0 && <p>No past invoices.</p>}
                    </List>
                </Paper>

                {invoices.length > 5 && !showAll ? (
                    <p
                        style={{ cursor: 'pointer', color: Colors.primaryColor, textAlign: 'center' }}
                        onClick={() => setShowAll(true)}
                    >
                        Show More
                    </p>
                ) : (
                    <p style={{ textAlign: 'center' }}>
                        If you're looking for an older invoice or can't find the one you need,
                        please <Link href="https://consultwithkyle.com/contact-us" target="_blank">let us know</Link>.
                    </p>
                )}
            </StyledSection>
        );
    };

    const renderBillingPreferencesSection = () => (
        <StyledSection title="Update Billing Preferences">
            <p>
                Looking to update your payment method or financial contact information?
            </p>
            {billingUrl ? (
                <ContainedButton onClick={() => showLinkInNewTab(billingUrl.url)} sx={{ marginTop: '20px' }}>
                    Update Billing Preferences
                </ContainedButton>
            ) : (
                <Skeleton height={50} width={200} />
            )}
        </StyledSection>
    );

    return (
        <StyledPage>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <StyledSubHeader title="Billing" />
                </Grid>
                <Grid item xs={12}>
                    {isLoading ? (
                        renderLoadingContent()
                    ) : (
                        <>
                            {renderPackageDetailsSection()}
                            {renderDueInvoicesSection()}
                            {renderInvoiceHistorySection()}
                            {(billingUrl || isLoading) && renderBillingPreferencesSection()}
                        </>
                    )}
                </Grid>
            </Grid>
        </StyledPage>
    );
};

export default observer(withRouter(ViewSiteBillingComponent));
